import moment from 'moment';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';

type CalendarMonthYear = { month: number; year: number };

function parseSubscriptionDate(value: string | undefined): moment.Moment | null {
  if (!value?.trim()) {
    return null;
  }
  const parsed = moment(ensureAsciiDateString(value) || value, 'YYYY-MM-DD', true);
  if (parsed.isValid()) {
    return parsed;
  }
  const loose = moment(ensureAsciiDateString(value) || value);
  return loose.isValid() ? loose : null;
}

/**
 * Initial calendar month/year based on the active subscription window.
 *
 * - No active subscription today or in the future -> `null` (caller uses current real month).
 * - Start date in a future month -> that future month.
 * - Start date in a past/current month but still active -> current (today's) month.
 */
export function getDefaultCalendarMonthYear(
  start_date: string | undefined,
  end_date?: string | undefined,
  reference: moment.Moment = moment(),
): CalendarMonthYear | null {
  const start = parseSubscriptionDate(start_date);
  if (!start) {
    return null;
  }

  const today = reference.clone().startOf('day');
  const end = parseSubscriptionDate(end_date);

  // Subscription is neither active today nor in the future -> use current real month.
  if (end && end.clone().startOf('day').isBefore(today, 'day')) {
    return null;
  }

  // Start month is in the future -> open on that future month.
  if (start.clone().startOf('month').isAfter(today, 'month')) {
    return { month: start.month(), year: start.year() };
  }

  // Start is in a past or the current month (still active) -> open on current month.
  return { month: today.month(), year: today.year() };
}
